import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import API_URL from "../config/apiConfig";
import { DEFAULT_MENU_THEME } from "../config/menuThemes";
import { resolveMediaUrl } from "../utils/mediaUrl";
import {
  getCartSummary,
  parseTableNumber,
  projectMenuItems,
} from "../services/customerMenuModel";

const readCart = (menuLink) => {
  try {
    const value = JSON.parse(localStorage.getItem(`cart_${menuLink}`));
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

export const assetUrl = (value, restaurantId, fallback) => {
  if (!value) return fallback;
  if (/^(https?:|data:|\/images\/)/.test(value)) return value;
  if (value.startsWith("/")) return `${API_URL}${value}`;
  if (value.startsWith("cover-")) return `/images/default-covers/${value}`;
  return `${API_URL}/uploads/restaurants/${restaurantId}/${value}`;
};

const useCustomerMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { menuLink } = useParams();
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);
  const [cart, setCart] = useState(() => readCart(menuLink));
  const [activeCategoryKey, setActiveCategoryKey] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setCart(readCart(menuLink));
    const tableNumber = parseTableNumber(location.search);
    if (tableNumber) localStorage.setItem(`tableNumber_${menuLink}`, String(tableNumber));
  }, [location.search, menuLink]);

  useEffect(() => {
    if (!menuLink) {
      setError("لینک منو معتبر نیست.");
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError("");
    fetch(`${API_URL}/api/menus/public/${encodeURIComponent(menuLink)}`, { signal: controller.signal })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "دریافت منو ناموفق بود.");
        setMenuData(data);
        localStorage.setItem(`theme_${menuLink}`, data.theme || DEFAULT_MENU_THEME);
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setError(requestError.message || "دریافت منو ناموفق بود.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [menuLink, retryKey]);

  // Item photos are stored as "/uploads/..." paths; resolve them once here so
  // every theme, the cart and the food detail page get a loadable URL.
  const categories = useMemo(() => (
    Array.isArray(menuData?.categories)
      ? menuData.categories.map((category) => ({
          ...category,
          items: (category.items || []).map((item) => ({
            ...item,
            image: resolveMediaUrl(item.image),
          })),
        }))
      : []
  ), [menuData]);
  const visibleItems = useMemo(() => projectMenuItems(categories, {
    categoryKey: activeCategoryKey,
    query: searchTerm,
  }), [activeCategoryKey, categories, searchTerm]);
  const quantities = useMemo(() => Object.fromEntries(cart.map((item) => [item._id, item.quantity])), [cart]);
  const cartSummary = useMemo(() => getCartSummary(cart), [cart]);

  const updateCart = (updater) => {
    setCart((current) => {
      const updated = updater(current.map((item) => ({ ...item })));
      localStorage.setItem(`cart_${menuLink}`, JSON.stringify(updated));
      return updated;
    });
  };

  const addItem = (item) => updateCart((current) => {
    const existing = current.find((cartItem) => cartItem._id === item._id);
    if (existing) existing.quantity += 1;
    else current.push({ ...item, quantity: 1 });
    return current;
  });

  const decreaseItem = (item) => updateCart((current) => {
    const existing = current.find((cartItem) => cartItem._id === item._id);
    if (!existing) return current;
    existing.quantity -= 1;
    return existing.quantity > 0 ? current : current.filter((cartItem) => cartItem._id !== item._id);
  });

  const openItem = (item) => {
    localStorage.setItem("selectedItem", JSON.stringify(item));
    localStorage.setItem("selectedMenuLink", menuLink);
    navigate(`/menu/${encodeURIComponent(menuLink)}/item/${item._id}${location.search}`);
  };

  const openCart = () => navigate(`/restaurant/cart/${encodeURIComponent(menuLink)}${location.search}`);

  const restaurant = menuData?.restaurant || {};
  const settings = restaurant.restaurantSettings || {};
  const profileImage = assetUrl(settings.profileImage || restaurant.logo, restaurant._id, "/images/default-profile.png");
  const coverImage = assetUrl(settings.coverImage || restaurant.cover, restaurant._id, "/images/default-cover.jpg");

  return {
    menuLink,
    menuData,
    restaurant,
    settings,
    profileImage,
    coverImage,
    theme: menuData?.theme || DEFAULT_MENU_THEME,
    showBamizbanBadge: menuData?.showBamizbanBadge ?? true,
    loading,
    error,
    retry: () => setRetryKey((value) => value + 1),
    categories,
    visibleItems,
    activeCategoryKey,
    setActiveCategoryKey,
    searchTerm,
    setSearchTerm,
    quantities,
    cartSummary,
    addItem,
    decreaseItem,
    openItem,
    openCart,
  };
};

export default useCustomerMenu;
